/**
 * Displays structured property details organized into tabs and labeled panels.
 *
 * The `PropertyDetailsSection` component renders one or more tabbed panels of property
 * information, substituting attribute values into templated HTML via `renderSafeHtml`.
 * It supports displaying attributes from both parcel data and related map layers,
 * using a configurable layout definition.
 *
 * When no property is selected or no attributes are available, it displays an
 * empty-state message prompting the user to perform a search or map selection.
 * 
 * -- Config.json in the config folder (propertyDetailsLayout) --
 * 
 */

import React, { useMemo, useState } from "react";
import AccordionSection, { AccordionSectionHandle } from "../../utils/accordion/AccordionSection";
import { renderSafeHtml } from "../../utils/RenderTemplate";
import "./styles/PropertyDetailsSection.css";

export type DetailsPanelSpec = {
  label: string;
  content: string;
  source?: "parcelsLayer" | "layersLayer";
  layerNumber?: number;
  layerUrl?: string;
};

export type DetailsTabSpec = {
  header: string;
  panels: DetailsPanelSpec[];
};

type Props = {
  layout: DetailsTabSpec[];
  attrs?: Record<string, any>;
  layerAttrs?: Record<string, Record<number, any[]>>;
  defaultLayersRoot?: string;
  defaultOpen?: boolean;
  sectionRef?: React.Ref<AccordionSectionHandle>;
};

const EMPTY_RE = /^(?:\s*|null|undefined)$/i;

const PropertyDetailsSection: React.FC<Props> = ({
  layout,
  attrs = {},
  layerAttrs = {},
  defaultLayersRoot,
  defaultOpen,
  sectionRef,
}) => {
  const [active, setActive] = useState(0);
  const tabs = useMemo(() => layout ?? [], [layout]);
  const multi = tabs.length > 1;

  const getAttrsForPanel = (p: DetailsPanelSpec): Record<string, any> | Record<string, any>[] => {
    if (p.source === "layersLayer" && typeof p.layerNumber === "number") {
      const root = (p.layerUrl || defaultLayersRoot || "").replace(/\/+$/, "");
      if (!root) return {};
      const arr = layerAttrs[root]?.[p.layerNumber];
      if (Array.isArray(arr) && arr.length) return arr;
      return {};
    }
    return attrs ?? {};
  };

  const isPanelEmpty = (p: DetailsPanelSpec) => {
    const rowAttrs = getAttrsForPanel(p);
    if (Array.isArray(rowAttrs)) {
      return rowAttrs.every((attrs) => {
        const html = renderSafeHtml(p.content ?? "", attrs);
        const text = html.replace(/<[^>]*>/g, "").trim();
        return EMPTY_RE.test(text);
      });
    }
    const html = renderSafeHtml(p.content ?? "", rowAttrs);
    const text = html.replace(/<[^>]*>/g, "").trim();
    return EMPTY_RE.test(text);
  };

  const hasAnyValue = useMemo(() => {
    const hasAttrs =
      Object.keys(attrs).length > 0 ||
      Object.keys(layerAttrs).some((root) => Object.keys(layerAttrs[root] || {}).length > 0);
    if (!hasAttrs) return false;

    for (const t of tabs) {
      for (const p of t.panels || []) {
        if (!isPanelEmpty(p)) return true;
      }
    }
    return false;
  }, [tabs, attrs, layerAttrs]);

  const renderRow = (p: DetailsPanelSpec) => {
    const rowAttrs = getAttrsForPanel(p);

    // Handle multiple results from layersLayer queries
    if (Array.isArray(rowAttrs)) {
      if (rowAttrs.length === 0) {
        return (
          <div className="pd-row" key={p.label}>
            <div className="pd-label">{p.label}</div>
            <div className="pd-value">
              <span className="pd-empty-value">No value available</span>
            </div>
          </div>
        );
      }

      // Render all results vertically with separators
      return (
        <div className="pd-row" key={p.label}>
          <div className="pd-label">{p.label}</div>
          <div className="pd-value">
            {rowAttrs.map((attrs, index) => {
              const html = renderSafeHtml(p.content ?? "", attrs);
              const textOnly = html.replace(/<[^>]*>/g, "").trim();
              const empty = EMPTY_RE.test(textOnly);

              if (empty) return null;

              return (
                <div key={index}>
                  {index > 0 && <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid #ddd" }} />}
                  <span dangerouslySetInnerHTML={{ __html: html }} />
                </div>
              );
            }).filter(Boolean)}
          </div>
        </div>
      );
    }

    // Handle single result
    const hasData = p.source === "layersLayer"
      ? Object.keys(rowAttrs).length > 0
      : Object.keys(rowAttrs).length > 0;

    if (!hasData) {
      return (
        <div className="pd-row" key={p.label}>
          <div className="pd-label">{p.label}</div>
          <div className="pd-value">
            <span className="pd-empty-value">No value available</span>
          </div>
        </div>
      );
    }

    const html = renderSafeHtml(p.content ?? "", rowAttrs);
    const textOnly = html.replace(/<[^>]*>/g, "").trim();
    const empty = EMPTY_RE.test(textOnly);

    return (
      <div className="pd-row" key={p.label}>
        <div className="pd-label">{p.label}</div>
        <div className="pd-value">
          {empty ? (
            <span className="pd-empty-value">No value available</span>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    );
  };

  return (
    <AccordionSection ref={sectionRef} title="Property Details" defaultOpen={defaultOpen}>
      {!tabs.length || !hasAnyValue ? (
        <div className="pd-empty">
          Use the search tools (above), or the map (right) to find and select a property.
          Once selected, property details will appear here.
        </div>
      ) : (
        <div className="pd">
          {multi && (
            <div className="pd-tabs" role="tablist" aria-label="Property detail sections">
              {tabs.map((t, i) => (
                <button
                  key={t.header + i}
                  role="tab"
                  aria-selected={i === active}
                  className={`pd-tab ${i === active ? "is-active" : ""}`}
                  onClick={() => setActive(i)}
                >
                  {t.header}
                </button>
              ))}
            </div>
          )}
          <div className="pd-panels">{(tabs[multi ? active : 0].panels || []).map(renderRow)}</div>
        </div>
      )}
    </AccordionSection>
  );
};

export default PropertyDetailsSection;